import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createInterviewResponse } from '../services/supabase';
import toast from 'react-hot-toast';
import { Loader2Icon } from 'lucide-react';

interface CandidateFormProps {
  interviewId: string;
  onComplete: (responseId: string) => void;
}

export function CandidateForm({ interviewId, onComplete }: CandidateFormProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const validateInput = () => {
    if (!name.trim() || !email.trim()) {
      toast.error(t('interview.candidate.requiredFields'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      toast.error(t('interview.candidate.invalidEmail'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateInput()) {
      return;
    }

    try {
      setLoading(true);
      
      const response = await createInterviewResponse(
        interviewId,
        name.trim(),
        email.trim().toLowerCase()
      );

      if (response?.id) {
        onComplete(response.id);
      } else {
        throw new Error('No response ID received');
      }
    } catch (error) {
      console.error('Error creating response:', error);
      toast.error(t('interview.candidate.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          {t('interview.candidate.name')}
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          required
          disabled={loading}
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
          {t('interview.candidate.email')}
        </label>
        <input
          type="email"
          id="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
          required
          disabled={loading}
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
      >
        {loading ? (
          <div className="flex items-center gap-2">
            <Loader2Icon className="h-5 w-5 animate-spin" />
            {t('interview.candidate.submitting')}
          </div>
        ) : (
          t('interview.candidate.start')
        )}
      </button>
    </form>
  );
}